import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-premium';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ButtonCellComponent,
  EmployeeCellComponent,
} from '~/components/SolaForceDataGrid';
import { useAllEmployees } from '~/data/employees';
import { Enum, EnumListKey, useAllEnums } from '~/data/enums';
import { useAllLegalEntities } from '~/data/legalEntity';
import { AbsencesReportData } from '~/data/reports';
import { useReportNavigateToView } from '~/scenes/PortalModules/Analytics/common/useReportNavigateToView';
import { People } from '~/services/Http';
import { getEnumName, getFormattedDateString } from '~/utils/helper';

import {
  AbsencesReportRow,
  AbsencesReportRow_Absences,
  AbsencesReportRow_Employee,
  AbsencesReportRow_PersonnelGroups,
  AbsencesReportRow_Position,
  convertAbsencesReportDataToRows,
} from './data';

type Props = {
  data: AbsencesReportData[];
};

export const useAbsencesReport = ({ data }: Props) => {
  const { t } = useTranslation();

  const { goToEmployeeBasicDataView } = useReportNavigateToView();
  const { getEmployeeById } = useAllEmployees();
  const { getEnums } = useAllEnums();
  const { legalEntities } = useAllLegalEntities();

  const getLegalEntityById = useMemo(() => {
    return (legalEntityId: number) => {
      return legalEntities.find((le) => le.fLegalEntityId === legalEntityId);
    };
  }, [legalEntities]);

  const reportEnums = useMemo(() => {
    return {
      absenceLongReason: getEnums(EnumListKey.ABSENCE_LONG_REASON),
      absenceShortReason: getEnums(EnumListKey.ABSENCE_SHORT_REASON),
      absenceStatus: getEnums(EnumListKey.ABSENCE_STATUS),
      personnelGroupNames: getEnums(EnumListKey.PERSONNEL_GROUP_NAME),
      personnelGroupEnumsByGroup: Object.fromEntries(
        [
          EnumListKey.PERSONNEL_GROUP_1,
          EnumListKey.PERSONNEL_GROUP_2,
          EnumListKey.PERSONNEL_GROUP_3,
          EnumListKey.PERSONNEL_GROUP_4,
          EnumListKey.PERSONNEL_GROUP_5,
        ].map((key) => {
          return [key, getEnums(key)];
        }),
      ),
    };
  }, [getEnums]);

  const { rows } = useMemo(() => {
    return {
      rows: convertAbsencesReportDataToRows({
        data,
        getEmployeeById,
        getLegalEntityById,
      }),
    };
  }, [data, getEmployeeById, getLegalEntityById]);

  const dataColumnOptions = useMemo(() => {
    return data.reduce(
      (acc, row) => {
        const { fEmpAbsenceLongHistory, fEmpAbsenceShortHistory } = row;

        const includeIfMissing = (options: string[], value: string) => {
          if (value && !options.includes(value)) {
            options.push(value);
          }
        };

        fEmpAbsenceShortHistory.forEach((absence) => {
          includeIfMissing(acc.shortReasonOptions, absence.fEmpAbsenceReason);
          includeIfMissing(acc.statusOptions, absence.fEmpAbsenceStatus);
        });

        fEmpAbsenceLongHistory.forEach((absence) => {
          includeIfMissing(acc.longReasonOptions, absence.fEmpAbsenceReason);
          includeIfMissing(acc.statusOptions, absence.fEmpAbsenceStatus);
        });
        return acc;
      },
      {
        shortReasonOptions: [],
        longReasonOptions: [],
        statusOptions: [],
      } as {
        shortReasonOptions: string[];
        longReasonOptions: string[];
        statusOptions: string[];
      },
    );
  }, [data]);

  const { columnConfigs, columnGroupingModel } = useMemo(() => {
    const {
      absenceLongReason,
      absenceShortReason,
      absenceStatus,
      personnelGroupNames,
      personnelGroupEnumsByGroup,
    } = reportEnums;

    const { shortReasonOptions, longReasonOptions, statusOptions } =
      dataColumnOptions;

    const formatDateCell = (date: Date | undefined) =>
      getFormattedDateString(date, 'L');

    const findDuplicateByCode = (enum2: Enum) => (enum1: Enum) =>
      enum2.code === enum1.code;

    const getEnumValueOptions = (
      enumOptions: Enum[],
      filterOptionCodes?: string[],
    ) => {
      const uniqueEnums = enumOptions.filter(
        (enum1, index, array) =>
          array.findIndex(findDuplicateByCode(enum1)) === index,
      );

      const availableEnums = filterOptionCodes
        ? uniqueEnums.filter(({ code }) => filterOptionCodes.includes(code))
        : uniqueEnums;

      return availableEnums.map((e) => e.name);
    };

    const getAbsenceType = (type: string) => {
      switch (type) {
        case 'SHORT':
          return t('laShortAbsence');
        case 'LONG':
          return t('laLongAbsence');
      }
    };

    const getAbsenceReason = (absenceType: string, reasonCode: string) => {
      switch (absenceType) {
        case People.AbsenceTypeEnum.SHORT:
          return getEnumName(absenceShortReason, reasonCode);
        case People.AbsenceTypeEnum.LONG:
          return getEnumName(absenceLongReason, reasonCode);
      }
    };

    const employeeColumnConfigs: GridColDef<AbsencesReportRow_Employee>[] = [
      {
        field: 'employeeId',
        headerName: t('laPicture'),
        filterable: false,
        sortable: false,
        disableExport: true,
        renderCell: (params) => {
          return (
            <EmployeeCellComponent
              {...params}
              pictureOnly={true}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 60,
      },
      {
        field: 'firstName',
        headerName: t('laFirstName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 90,
      },
      {
        field: 'lastName',
        headerName: t('laLastName'),
        hideable: false,
        renderCell: (params) => {
          return (
            <ButtonCellComponent
              {...params}
              onClick={() => {
                goToEmployeeBasicDataView(params.row.employeeId);
              }}
            />
          );
        },
        minWidth: 90,
      },
      {
        field: 'employeeNumber',
        headerName: t('laEmpId'),
        valueGetter: ({ row: { employeeId } }) =>
          getEmployeeById(employeeId)?.fEmpPersonNumber,
        minWidth: 130,
      },
    ];

    const positionColumnConfigs: GridColDef<AbsencesReportRow_Position>[] = [
      {
        field: 'organizationUnit',
        headerName: t('laOrgUnit'),
        minWidth: 150,
      },
      {
        field: 'organizationUnitNumber',
        headerName: t('laUnitNumber'),
        minWidth: 130,
      },
      {
        field: 'manager',
        headerName: t('laManager'),
        minWidth: 150,
      },
      {
        field: 'managerNumber',
        headerName: t('laManagerNumber'),
        minWidth: 150,
      },
      { field: 'legalEntity', headerName: t('laLegalEntity'), minWidth: 180 },
      { field: 'costCenter', headerName: t('laCostCenter'), minWidth: 210 },
      {
        field: 'costCenterCode',
        headerName: t('laCostCenterCode'),
        minWidth: 150,
      },
    ];

    const absencesColumnConfig: GridColDef<AbsencesReportRow_Absences>[] = [
      {
        field: 'absencesDaysInPeriod',
        headerName: t('laAbsenceDaysInPeriod'),
        minWidth: 200,
      },
      {
        field: 'absenceDaysTotal',
        headerName: t('laAbsenceDaysTotal'),
        minWidth: 150,
      },
      {
        field: 'type',
        headerName: t('laType'),
        type: 'singleSelect',
        valueGetter: ({ value }) => getAbsenceType(value),
        valueOptions: [t('laShortAbsence'), t('laLongAbsence')],
        minWidth: 130,
      },
      {
        field: 'startDate',
        type: 'date',
        headerName: t('laStartDate'),
        valueFormatter: ({ value }) => formatDateCell(value),
        minWidth: 130,
      },
      {
        field: 'endDate',
        type: 'date',
        headerName: t('laEndDate'),
        valueFormatter: ({ value }) => formatDateCell(value),
        minWidth: 130,
      },
      {
        field: 'reason',
        headerName: t('laReason'),
        type: 'singleSelect',
        valueGetter: ({ row, value }) => getAbsenceReason(row.type, value),
        valueOptions: Array.from(
          new Set([
            ...getEnumValueOptions(absenceShortReason, shortReasonOptions),
            ...getEnumValueOptions(absenceLongReason, longReasonOptions),
          ]),
        ),
        minWidth: 150,
      },
      {
        field: 'info',
        headerName: t('laInfo'),
        minWidth: 130,
      },
      {
        field: 'status',
        headerName: t('laStatus'),
        type: 'singleSelect',
        valueGetter: ({ value }) => getEnumName(absenceStatus, value),
        valueOptions: getEnumValueOptions(absenceStatus, statusOptions),
        minWidth: 110,
      },
    ];

    const personnelGroupColumnConfigs: GridColDef<AbsencesReportRow_PersonnelGroups>[] =
      [
        EnumListKey.PERSONNEL_GROUP_1,
        EnumListKey.PERSONNEL_GROUP_2,
        EnumListKey.PERSONNEL_GROUP_3,
        EnumListKey.PERSONNEL_GROUP_4,
        EnumListKey.PERSONNEL_GROUP_5,
      ].map((key, index) => {
        const pgEnums = personnelGroupEnumsByGroup[key] ?? [];

        return {
          field: `personnelGroup${index + 1}`,
          headerName: getEnumName(personnelGroupNames, key),
          valueGetter: ({ value }) => getEnumName(pgEnums, value),
          type: 'singleSelect',
          valueOptions: getEnumValueOptions(pgEnums),
          flex: 1,
          minWidth: 140,
        };
      });

    const columnConfigs = [
      ...employeeColumnConfigs,
      ...absencesColumnConfig,
      ...positionColumnConfigs,
      ...personnelGroupColumnConfigs,
    ] as GridColDef<AbsencesReportRow>[];

    const getFieldNamesFromColumnConfig = (gridColDefs: GridColDef[]) =>
      gridColDefs.map(({ field }) => ({ field: field }));

    const columnGroupingModel: GridColumnGroupingModel = [
      {
        groupId: 'employee',
        headerName: t('laEmployee'),
        children: getFieldNamesFromColumnConfig(employeeColumnConfigs),
      },
      {
        groupId: 'absences',
        headerName: t('laAbsences'),
        children: getFieldNamesFromColumnConfig(absencesColumnConfig),
      },
      {
        groupId: 'position',
        headerName: t('laPosition'),
        children: getFieldNamesFromColumnConfig(positionColumnConfigs),
      },
      {
        groupId: 'personnelGroups',
        headerName: t('laPersonnelGroups'),
        children: getFieldNamesFromColumnConfig(personnelGroupColumnConfigs),
      },
    ];

    return {
      columnConfigs,
      columnGroupingModel,
    };
  }, [
    t,
    reportEnums,
    dataColumnOptions,
    goToEmployeeBasicDataView,
    getEmployeeById,
  ]);

  return {
    table: {
      columnConfigs,
      rows,
      columnGroupingModel,
    },
  };
};
